import type { ScreenerColumn } from "epsm-api-types/models";
import { type Dispatch, useCallback } from "react";
import type { ViewState } from "@/context/WidgetViewContext";
import type { ViewId } from "@/shared/types/common";

export const useColumnMenuItems = (
	widgetName: ViewId,
	screenerColumns: ScreenerColumn[],
	setVisibleColumns: Dispatch<
		React.SetStateAction<ViewState<ScreenerColumn[]>>
	>,
) => {
	return useCallback(() => {
		const removeColumn = (name: string) => {
			const [columnName, subFieldName] = name.split(".");
			setVisibleColumns((prevState) => {
				if (subFieldName) {
					return {
						...prevState,
						[widgetName]: prevState[widgetName]
							.map((column) => {
								if (column.name === columnName) {
									const subFields = column.subFields?.filter(
										({ name }) => name !== subFieldName,
									);
									return subFields?.length
										? { ...column, subFields }
										: undefined;
								}
								return column;
							})
							.filter(
								(column): column is ScreenerColumn => column !== undefined,
							),
					};
				}

				return {
					...prevState,
					[widgetName]:
						prevState[widgetName]?.filter(
							(column) => column.name !== columnName,
						) || [],
				};
			});
		};

		const items: Record<string, string> = {};
		for (const column of screenerColumns) {
			const columnTitle =
				column.titleAbbreviation || column.titleShort || column.title;
			if (column.subFields?.length) {
				for (const subField of column.subFields) {
					const subFieldTitle = subField.titleShort || subField.title;
					items[`${column.name}.${subField.name}`] =
						`${columnTitle} ${subFieldTitle}`;
				}
			} else {
				items[column.name] = columnTitle;
			}
		}

		return Object.keys(items).map((name) => {
			return {
				key: name,
				label: items[name],
				hideMenuOnAction: true,
				onClick: () => removeColumn(name),
			};
		});
	}, [widgetName, screenerColumns, setVisibleColumns]);
};
